import { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import BlogCard from '../components/blog/BlogCard';
import { fetchBlogPosts } from '../services/blogService';
import '../styles/Blog.css';

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const BUCKET_URL = process.env.REACT_APP_BUCKET_URL;

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const postsData = await fetchBlogPosts(BUCKET_URL);
        setPosts(postsData);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setPosts([]);
      }
    };

    loadPosts();
  }, [BUCKET_URL]);

  const allTags = useMemo(() => {
    const tags = new Set();
    posts.forEach(post => {
      if (post.tags) {
        post.tags.forEach(tag => tags.add(tag));
      }
    });
    return Array.from(tags).sort();
  }, [posts]);

  const filteredPosts = useMemo(() => {
    return posts.filter(post => {
      const matchesSearch = 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
        (post.excerpt && post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesTag = selectedTag ? (post.tags && post.tags.includes(selectedTag)) : true;
      
      return matchesSearch && matchesTag;
    });
  }, [posts, searchQuery, selectedTag]);

  return (
    <Layout>
      <div className="blog-container">
        <div className="blog-content">
          <div className="blog-intro">
            <h1 className="blog-intro-title">Blog & Insights</h1>
            <p className="blog-intro-text">
              Welcome to my corner of the web where I share thoughts on machine learning
              engineering practices and related topics. Through these posts, I aim to 
              document my journey, share valuable insights, and contribute to the broader 
              tech community.
            </p>
          </div>

          <div className="blog-filters">
            <div className="blog-search">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="search-icon">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input 
                type="text" 
                placeholder="Search articles..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            {allTags.length > 0 && (
              <div className="blog-tag-filter">
                <button 
                  className={`filter-tag ${selectedTag === null ? 'active' : ''}`}
                  onClick={() => setSelectedTag(null)}
                >
                  All
                </button>
                {allTags.map(tag => (
                  <button 
                    key={tag}
                    className={`filter-tag ${selectedTag === tag ? 'active' : ''}`}
                    onClick={() => setSelectedTag(tag === selectedTag ? null : tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="blog-grid">
            {filteredPosts.length > 0 ? (
              filteredPosts.map((post) => (
                <BlogCard key={post.filename} post={post} />
              ))
            ) : (
              <div className="blog-empty-state">
                <p>No posts found matching your criteria.</p>
                <button onClick={() => { setSearchQuery(''); setSelectedTag(null); }}>Clear Filters</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
